import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChatCircleDots, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { setRead } from "@/lib/commentReads";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function CommentsThread({ api, postId, currentName }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/posts/${postId}/comments`)
      .then((res) => { if (active) { setComments(res.data); setRead(postId, res.data.length); } })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/posts/${postId}/comments`, { text, author_name: currentName });
      const next = [...comments, res.data];
      setComments(next);
      setRead(postId, next.length);
      setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to comment"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid={`comments-${postId}`} className="mt-2">
      <div className="flex items-center gap-1.5 text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-2">
        <ChatCircleDots size={14} /> Discussion {comments.length > 0 && `(${comments.length})`}
      </div>
      <div className="space-y-2 max-h-52 overflow-y-auto">
        {loading ? (
          <p className="text-[#a3b0b0] text-xs">Loading…</p>
        ) : comments.length === 0 ? (
          <p className="text-[#a3b0b0] text-xs">No comments yet. Start the discussion.</p>
        ) : (
          comments.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`} className="bg-[#fafafa] border border-[#eef1f2] rounded-[10px] p-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#3C4048] text-xs font-bold">{c.author_name}</span>
                <span className="text-[#a3b0b0] text-[10px]">{timeAgo(c.created_at)}</span>
              </div>
              <p className="text-[#3C4048] text-sm mt-0.5 whitespace-pre-line break-words">{c.text}</p>
            </div>
          ))
        )}
      </div>
      <div className="flex items-end gap-2 mt-2">
        <Textarea
          data-testid={`comment-input-${postId}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          placeholder="Add feedback… (⌘/Ctrl+Enter to send)"
          className="border-[#e5e7eb] text-[#3C4048] min-h-[40px] text-sm flex-1"
        />
        <Button data-testid={`comment-send-${postId}`} onClick={send} disabled={sending || !text.trim()} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[10px] h-10 px-3">
          <PaperPlaneRight size={16} weight="fill" />
        </Button>
      </div>
    </div>
  );
}
